import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from '@orion-platform/core';
import { environment } from '@orion/environment';
import { Helper } from 'apps/orion/src/app/helper/helper';
import { Observable } from 'rxjs';
import {
  BuyersPartnumbers,
  BuyersPartnumbersResult,
  ProjectionFilter,
  ProjectionPrecalculate,
} from '../models/mm-buyers-partnumbers.model';
import { FilterParams, PnVendorCode } from '../models/supply-visibility.model';
import {
  DashboardPanelModel,
  PanelCache,
  SortCacheValue,
  SortingMap,
} from '../models/sv-dashboard';

@Injectable({
  providedIn: 'root',
})
export class BuyersPartnumbersListService {
  private configService = inject(ConfigService);
  private http = inject(HttpClient);

  private apiUrl!: string;

  constructor() {
    this.apiUrl = this.configService.getSettings('apiBaseUrl') as string;
  }

  getBuyersPartnumbersList(buyer: string): Observable<BuyersPartnumbers[]> {
    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.getBuyersPartNumbersList}`;

    let params = new HttpParams();
    params = params.append('AvlBuyer', buyer);

    return this.http.get<BuyersPartnumbers[]>(url, { params: params });
  }

  getPNsPrecalculateProjectionByFilter(filter: FilterParams): Observable<BuyersPartnumbersResult> {
    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.getPNsPrecalculateProjectionByFilter}`;

    let filterParams: ProjectionFilter = {};
    if (filter.filterSearch) {
      filterParams = {
        customQuery: filter.customQuery as string,
        filterBuyers: filter.buyers || [],
        filterContacts: filter.contacts
          ? filter.contacts /*contact.split(/\s+|,/).map((c) => (c ? c.trim() : '')).filter((c) => c) */
          : [],
        filterDeliveryTerms: filter.filterDeliveryTerms || [],
        filterExcludeMaterialGroups: filter.filterExcludeMaterialGroups || [],
        filterExcludeVendorCodes: filter.filterExcludeVendorCodes || [],
        filterFlags: filter.flags || [],
        filterLeadtimeQuery: filter.filterLeadtimeQuery || undefined,
        filterMaterialGroups: filter.materialGroups || [],
        filterPartNumbersVendorCodes: [],
        filterQuery: filter.formula as string,
        filterReviewed: undefined,
        filterSearch: Helper.virtualVCReplace(filter.filterSearch) || undefined,
        filterTaxCodes: filter.filterTaxCodes || [],
        filterVariant: filter.filterVariant as string,
        filterVendorCodes: filter.vendors || [],
        filterVirtualVC: filter.filterVirtualVC as boolean,
        filterVirtualVCIncludeChild: filter.filterVirtualVCIncludeChild as boolean,
        fulltextSearch: filter.fulltextSearch,
        loadVendorName: filter.loadVendorName,
      };
    } else {
      filterParams = {
        customQuery: filter.customQuery as string,
        filterBuyers: filter.buyers || [],
        //filterContacts: contact ? contact.split(/\s+|,/).map((c) => (c ? c.trim() : '')).filter((c) => c) : [],
        filterContacts: filter.contacts
          ? filter.contacts /*contact.split(/\s+|,/).map((c) => (c ? c.trim() : '')).filter((c) => c) */
          : [],
        filterDeliveryTerms: filter.filterDeliveryTerms || [],
        filterExcludeMaterialGroups: filter.filterExcludeMaterialGroups || [],
        filterExcludeVendorCodes: filter.filterExcludeVendorCodes || [],
        filterFlags: filter.flags || [],
        filterLeadtimeQuery: filter.filterLeadtimeQuery as string,
        filterMaterialGroups: filter.materialGroups || [],
        filterPartNumbersVendorCodes: filter.partNumbers || [],
        filterQuery: filter.formula as string,
        filterReviewed: undefined,
        filterTaxCodes: filter.filterTaxCodes || [],
        filterVariant: filter.filterVariant as string,
        filterVendorCodes: filter.vendors || [],
        filterVirtualVC: filter.filterVirtualVC as boolean,
        filterVirtualVCIncludeChild: filter.filterVirtualVCIncludeChild as boolean,
        fulltextSearch: filter.fulltextSearch,
        loadVendorName: filter.loadVendorName,
      };
    }
    filterParams.filterPartNumbersVendorCodes = this.partNumberVendorCodesToUpper(
      filterParams?.filterPartNumbersVendorCodes ?? [],
    );
    filterParams.filterVendorCodes = this.vendorsToUpper(filterParams.filterVendorCodes ?? []);
    filterParams.filterExcludeVendorCodes = this.vendorsToUpper(
      filterParams.filterExcludeVendorCodes ?? [],
    );

    let params = new HttpParams();
    if (filter.sorting) {
      params = params.append('$orderBy', filter.sorting);
    }

    return this.http.post<BuyersPartnumbersResult>(url, filterParams, { params: params });
  }

  partNumberVendorCodesToUpper(partNumbersVendorCodes: PnVendorCode[]): PnVendorCode[] {
    const pnsVendors: PnVendorCode[] = [...partNumbersVendorCodes];
    if (partNumbersVendorCodes && partNumbersVendorCodes.length > 0) {
      for (let i = 0; i < pnsVendors.length; i++) {
        pnsVendors[i].vendorCode = pnsVendors[i].vendorCode
          ? Helper.virtualVCReplace(pnsVendors[i].vendorCode ?? '')
          : undefined;
      }
    }
    return pnsVendors;
  }

  vendorsToUpper(vendors: string[]): string[] {
    for (let i = 0; i < vendors.length; i++) {
      if (vendors[i] != null && vendors[i] != undefined) {
        vendors[i] = Helper.virtualVCReplace(vendors[i]);
      }
    }
    return vendors;
  }

  getPrecalculateProjection(): Observable<ProjectionPrecalculate> {
    let params = new HttpParams();
    params = params.append('variant', 'Weekly');
    return this.http.get<ProjectionPrecalculate>(
      `${this.getEndpoint('GetPrecalculateProjection')}`,
      {
        params: params,
      },
    );
  }

  getPrecalculateProjections(skip = '0', top = '1'): Observable<unknown> {
    let params = new HttpParams();
    params = params.append('$skip', skip);
    params = params.append('$top', top);
    return this.http.get(`${this.getEndpoint('GetPrecalculateProjections')}`, {
      params: params,
    });
  }

  getEndpoint(action: string): string {
    return `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.supplyVisibility}${'/'}${action}`;
  }

  convertSortingString(data: DashboardPanelModel): string {
    // Check if panelCache exists and contains the sort key
    const panelCache: PanelCache[] =
      data.panelCache && Array.isArray(data.panelCache) ? data.panelCache : [];
    const sortCache = panelCache.find((item): boolean => item.key === 'sort');

    if (!sortCache || !sortCache.value) {
      return '';
    }

    const { key, type } = sortCache.value as SortCacheValue;

    const sortingMap: SortingMap = {
      partNumber: { ascending: 'PartNumber asc', descending: 'PartNumber desc' },
      vendorCode: { ascending: 'VendorCode asc', descending: 'VendorCode desc' },
      virtualPN: {
        ascending: 'SubItems asc, VendorCode asc',
        descending: 'SubItems desc, VendorCode desc',
      },
    };

    const sortOrder = sortingMap[key] && sortingMap[key]?.[type];

    return sortOrder || '';
  }
}
